import { delegate } from '../lib/dom.js';
import { read, write } from '../lib/storage.js';
import { AP_DATA } from '../data/listings.js';
import { initRevealOnScroll } from './entrance-animations.js';
import { openModal } from './modal.js';
import { t, sprintf } from '../lib/i18n.js';

const KEY = 'autoplaza:favs';
const SYNC_KEY = 'autoplaza:favs:user';
const GUEST_KEY = 'autoplaza:favs:guest'; // cars hearted while logged out (see reconcileFavs)
const MAX_FAVS = 20; // hard cap on saved cars (mirrored server-side in rest-api.php)
let favSet = new Set(read(KEY, []));

// Lightweight self-styled toast (no CSS dependency) for the favorites cap.
function favToast(msg) {
  const el = document.createElement('div');
  el.textContent = msg;
  el.setAttribute('role', 'status');
  el.style.cssText = 'position:fixed;left:50%;bottom:24px;transform:translateX(-50%);z-index:99999;background:#1d1e2f;color:#fff;padding:12px 18px;border-radius:10px;font-size:14px;line-height:1.35;box-shadow:0 8px 24px rgba(0,0,0,.28);max-width:90vw;text-align:center;opacity:0;transition:opacity .2s ease';
  document.body.appendChild(el);
  requestAnimationFrame(() => { el.style.opacity = '1'; });
  setTimeout(() => { el.style.opacity = '0'; setTimeout(() => el.remove(), 260); }, 3200);
}

/* ---- account sync -------------------------------------------------------- */

function apiBase() { return (AP_DATA && AP_DATA.apiBase) || ''; }
function currentUserId() { return (AP_DATA && AP_DATA.userId) ? Number(AP_DATA.userId) : 0; }
function serverFavs() {
  const f = (AP_DATA && Array.isArray(AP_DATA.favorites)) ? AP_DATA.favorites : [];
  return f.map(Number).filter((n) => Number.isFinite(n));
}

// Push the full favorites set to the account. No-op for anonymous visitors.
//
// The account copy is what the e-mail notifications read, so a write that
// silently fails means mails about a car the user already un-hearted. Hence:
//  - one request in flight at a time; toggles made meanwhile are folded into
//    ONE follow-up push of the latest set (fast toggles can't land out of order);
//  - a 403 (the REST nonce expired - a tab left open past ~24h) fetches a fresh
//    nonce from admin-ajax `rest-nonce` and retries once;
//  - anything still failing tells the user instead of pretending it saved.
// The server enforces the same MAX_FAVS cap (defense-in-depth): a 409 surfaces
// the limit toast.
let pushing = false;
let pushAgain = false;

function postFavs(ids) {
  return fetch(`${apiBase()}favorites`, {
    method: 'POST',
    headers: Object.assign({ 'Content-Type': 'application/json' }, AP_DATA.nonce ? { 'X-WP-Nonce': AP_DATA.nonce } : {}),
    credentials: 'same-origin',
    body: JSON.stringify({ ids }),
  });
}

async function refreshNonce() {
  if (!AP_DATA.ajaxUrl) return false;
  try {
    const res = await fetch(`${AP_DATA.ajaxUrl}?action=rest-nonce`, { credentials: 'same-origin' });
    const nonce = res.ok ? (await res.text()).trim() : '';
    if (!/^[a-f0-9]{6,}$/i.test(nonce)) return false;
    AP_DATA.nonce = nonce;
    return true;
  } catch { return false; }
}

async function pushFavs() {
  if (!currentUserId() || !apiBase()) return;
  if (pushing) { pushAgain = true; return; }
  pushing = true;
  try {
    let res = await postFavs(Array.from(favSet));
    if (res.status === 403 && await refreshNonce()) res = await postFavs(Array.from(favSet));
    if (res.status === 409) {
      favToast(sprintf(t('fav_limit', 'Ai atins limita de %d de mașini favorite.'), MAX_FAVS));
    } else if (!res.ok) {
      favToast(t('fav_sync_failed', 'Nu am putut salva favoritele în cont. Reîncarcă pagina și încearcă din nou.'));
    }
  } catch {
    favToast(t('fav_sync_failed', 'Nu am putut salva favoritele în cont. Reîncarcă pagina și încearcă din nou.'));
  } finally {
    pushing = false;
    if (pushAgain) { pushAgain = false; pushFavs(); }
  }
}

// Reconcile localStorage favorites with the account on load.
//
// The local list after a logout is a STALE copy of the account, so merging all
// of it back at the next login resurrected cars removed on another device (and
// with them the e-mails). Instead the device remembers which cars were hearted
// while logged OUT (GUEST_KEY) and only those join the account at login:
//  - anonymous: keep the account marker; start a guest-adds list (seeded with
//    the whole local list only when no account ever synced here - pure guest
//    favorites, or a list from before this fix);
//  - logged in, same account, no guest adds: adopt the account set (so add /
//    remove on other devices propagate here);
//  - otherwise: account set + guest adds, pushed up if that added anything.
function reconcileFavs() {
  const uid = currentUserId();
  let syncedFor = null;
  try { syncedFor = localStorage.getItem(SYNC_KEY); } catch { /* ignore */ }

  if (!uid) {
    if (read(GUEST_KEY, null) === null) write(GUEST_KEY, syncedFor ? [] : Array.from(favSet));
    return;
  }

  let guest = read(GUEST_KEY, null);
  if (!Array.isArray(guest)) guest = syncedFor === String(uid) ? [] : Array.from(favSet);
  guest = guest.map(Number).filter((n) => Number.isFinite(n));

  const serverArr = serverFavs();
  const serverSet = new Set(serverArr);
  favSet = new Set(serverArr); // account is the source of truth
  let added = false;
  guest.forEach((id) => {
    if (!serverSet.has(id) && favSet.size < MAX_FAVS) { favSet.add(id); added = true; }
  });
  write(KEY, Array.from(favSet));
  try { localStorage.setItem(SYNC_KEY, String(uid)); localStorage.removeItem(GUEST_KEY); } catch { /* ignore */ }
  if (added) pushFavs(); // the account was missing cars hearted while logged out
}

const REDUCED = typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const HEART_SVG = '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 21s-7-4.35-9.5-9a5.5 5.5 0 0 1 9.5-5 5.5 5.5 0 0 1 9.5 5C19 16.65 12 21 12 21z"/></svg>';

// Floating-hearts burst - fires when a favorite is toggled ON.
// Six tiny hearts float upward in a slight fan, fade out, and self-clean.
function burstHearts(srcEl) {
  if (REDUCED) return;
  const rect = srcEl.getBoundingClientRect();
  const cx = rect.left + rect.width / 2;
  const cy = rect.top + rect.height / 2;
  const layer = ensureBurstLayer();
  const COUNT = 6;
  for (let i = 0; i < COUNT; i++) {
    const h = document.createElement('span');
    h.className = 'ap-fav-heart';
    h.innerHTML = HEART_SVG;
    // spread horizontally between -36px and +36px from center
    const dx = (i / (COUNT - 1) - 0.5) * 72 + (Math.random() - 0.5) * 12;
    // float upward 70-110px
    const dy = -(70 + Math.random() * 40);
    const rot = (Math.random() - 0.5) * 36;
    const scale = 0.7 + Math.random() * 0.5;
    const delay = i * 30;
    h.style.left = `${cx}px`;
    h.style.top = `${cy}px`;
    h.style.setProperty('--ap-fav-dx', `${dx.toFixed(1)}px`);
    h.style.setProperty('--ap-fav-dy', `${dy.toFixed(1)}px`);
    h.style.setProperty('--ap-fav-rot', `${rot.toFixed(1)}deg`);
    h.style.setProperty('--ap-fav-scale', scale.toFixed(2));
    h.style.animationDelay = `${delay}ms`;
    layer.appendChild(h);
    h.addEventListener('animationend', () => h.remove(), { once: true });
  }
}

let burstLayerEl = null;
function ensureBurstLayer() {
  if (burstLayerEl && document.body.contains(burstLayerEl)) return burstLayerEl;
  burstLayerEl = document.createElement('div');
  burstLayerEl.className = 'ap-fav-burst';
  burstLayerEl.setAttribute('aria-hidden', 'true');
  document.body.appendChild(burstLayerEl);
  return burstLayerEl;
}

// Pulse the heart icon itself with a quick scale bounce on toggle
function pulse(el) {
  if (REDUCED) return;
  el.classList.remove('is-pulsing');
  // Force reflow to restart the animation
  void el.offsetWidth;
  el.classList.add('is-pulsing');
  el.addEventListener('animationend', () => el.classList.remove('is-pulsing'), { once: true });
}

export function getFavs() {
  return favSet;
}

export function isFav(id) {
  return favSet.has(id);
}

export function toggleFav(id) {
  if (favSet.has(id)) {
    favSet.delete(id);
  } else {
    // Cap the number of saved cars; block the add and tell the user.
    if (favSet.size >= MAX_FAVS) {
      favToast(sprintf(t('fav_limit', 'Ai atins limita de %d de mașini favorite.'), MAX_FAVS));
      return false;
    }
    favSet.add(id);
  }
  write(KEY, Array.from(favSet));
  // Logged out: remember the hearts made now - only these join the account at
  // the next login (see reconcileFavs).
  if (!currentUserId()) {
    const guest = new Set(read(GUEST_KEY, []));
    if (favSet.has(id)) guest.add(id); else guest.delete(id);
    write(GUEST_KEY, Array.from(guest));
  }
  syncDOM(id);
  pushFavs(); // persist to the account (no-op when logged out)
  return favSet.has(id);
}

function syncBadges() {
  // Notification-style badge - show "9+" once the count exceeds 9.
  const total = favSet.size;
  const label = total > 9 ? '9+' : String(total);
  document.querySelectorAll('[data-role="fav-count"]').forEach((el) => {
    if (total === 0) {
      el.hidden = true;
      el.textContent = '';
    } else {
      el.hidden = false;
      el.textContent = label;
      el.classList.toggle('is-overflow', total > 9);
    }
  });
}

function syncDOM(id) {
  document.querySelectorAll(`[data-action="favorite"][data-id="${id}"]`).forEach((el) => {
    el.classList.toggle('is-fav', favSet.has(id));
    el.setAttribute('aria-pressed', String(favSet.has(id)));
  });
  // Count actually changed → update the badge here (NOT inside
  // syncAllVisible, which is called by the global MutationObserver and
  // would otherwise loop: textContent writes are childList mutations).
  syncBadges();
}

function syncAllVisible() {
  document.querySelectorAll('[data-action="favorite"][data-id]').forEach((el) => {
    const id = Number(el.dataset.id);
    el.classList.toggle('is-fav', favSet.has(id));
    el.setAttribute('aria-pressed', String(favSet.has(id)));
  });
  // No badge update here - favSet didn't change, the count is unchanged,
  // and writing to textContent here would feed back into the observer.
}

// Account page ("Contul meu") - render the saved cars into the favorites grid
// by fetching their cards from the REST /listings?include= endpoint. No-op on
// any page without the favorites container.
async function populateAccountFavorites() {
  const root = document.querySelector('[data-component="account-favorites"]');
  if (!root) return;
  const grid = root.querySelector('[data-role="favorites-grid"]');
  const loading = root.querySelector('[data-role="favorites-loading"]');
  const empty = root.querySelector('[data-role="favorites-empty"]');
  const total = root.querySelector('[data-role="favorites-total"]');
  // Standalone /favorites/ page: the "Send to friend" button is hidden in the
  // markup and only shown once we know saved cars actually rendered.
  const shareBtn = root.querySelector('[data-role="favorites-share-btn"]');

  const showEmpty = () => {
    if (loading) loading.hidden = true;
    if (grid) grid.hidden = true;
    if (total) total.hidden = true;
    if (empty) empty.hidden = false;
    if (shareBtn) shareBtn.hidden = true;
  };

  // favSet keeps insertion order (oldest → newest). Reverse so the MOST
  // RECENTLY added favorite renders first: the REST /listings?include= grid
  // preserves the given id order (orderby=post__in), and this matches the
  // logged-in account view, which is already newest-first
  // (atpl_fav_list() → ORDER BY created_at DESC).
  const ids = Array.from(favSet).filter((n) => Number.isFinite(n)).reverse();
  const apiBase = AP_DATA && AP_DATA.apiBase;
  if (!ids.length || !apiBase || !grid) { showEmpty(); return; }

  try {
    const res = await fetch(`${apiBase}listings?include=${ids.join(',')}`, {
      headers: AP_DATA.nonce ? { 'X-WP-Nonce': AP_DATA.nonce } : {},
      credentials: 'same-origin',
    });
    if (!res.ok) { showEmpty(); return; }
    const data = await res.json();
    if (!data || !data.total || !data.html) { showEmpty(); return; }
    grid.innerHTML = data.html;
    grid.hidden = false;
    if (loading) loading.hidden = true;
    if (empty) empty.hidden = true;
    if (total) { total.hidden = false; total.textContent = String(data.total); }
    if (shareBtn) shareBtn.hidden = false; // saved cars exist → allow sharing
    // The injected cards carry data-anim="rise" (opacity:0 until revealed) -
    // observe them now, otherwise they stay invisible on the account page.
    initRevealOnScroll(grid);
    syncAllVisible(); // light up the hearts on the rendered cards
  } catch {
    showEmpty();
  }
}

/* ---- "Send to friend" (shareable favorites list) ------------------------- */

// Build the platform share URL for a given option key. `url`/`title` are the raw
// (unencoded) share link + its human title; each target gets the encoding it needs.
function shareOptionHref(opt, url, title) {
  const u = encodeURIComponent(url);
  const tRaw = encodeURIComponent(title);          // for query params (subject/text)
  const tSpace = encodeURIComponent(`${title} `);  // "title " prefix, keeps a real space before the url
  switch (opt) {
    case 'whatsapp': return `https://wa.me/?text=${tSpace}${u}`;
    case 'telegram': return `https://t.me/share/url?url=${u}&text=${tRaw}`;
    case 'viber': return `viber://forward?text=${tSpace}${u}`;
    case 'email': return `mailto:?subject=${tRaw}&body=${tSpace}${u}`;
    case 'sms': return `sms:?&body=${tSpace}${u}`;
    default: return url;
  }
}

// Fill the share-options modal's option hrefs + link field with THIS share, then
// open it. Returns the dialog (or null when the modal isn't on the page).
function openShareModal(url, title) {
  const modal = document.getElementById('fav-share-modal');
  if (!modal) return null;

  modal.querySelectorAll('[data-share-opt]').forEach((el) => {
    const opt = el.dataset.shareOpt;
    if (opt === 'copy') return; // copy is a button handled on click
    el.setAttribute('href', shareOptionHref(opt, url, title));
  });

  // Reset the copy button label + stash the raw url on the modal for the copy handler.
  modal.dataset.shareUrl = url;
  const copyLabel = modal.querySelector('[data-role="copy-label"]');
  if (copyLabel && !copyLabel.dataset.default) copyLabel.dataset.default = copyLabel.textContent;
  if (copyLabel && copyLabel.dataset.default) copyLabel.textContent = copyLabel.dataset.default;

  const urlField = modal.querySelector('[data-role="fav-share-url"]');
  if (urlField) urlField.value = url;

  openModal(modal);
  return modal;
}

// Wire the modal's option clicks. The plain-link options (email/whatsapp/…) just
// follow their href; only Copy needs JS (clipboard + a confirmation label).
function initShareModalOptions() {
  delegate(document, 'click', '#fav-share-modal [data-share-opt="copy"]', (e, btn) => {
    e.preventDefault();
    const modal = btn.closest('#fav-share-modal');
    const url = modal ? modal.dataset.shareUrl || '' : '';
    if (!url) return;
    const label = btn.querySelector('[data-role="copy-label"]');
    const done = () => {
      if (!label) return;
      if (!label.dataset.default) label.dataset.default = label.textContent;
      label.textContent = t('fav_link_copied', 'Link copiat!');
      btn.classList.add('is-copied');
      setTimeout(() => {
        label.textContent = label.dataset.default;
        btn.classList.remove('is-copied');
      }, 2400);
    };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(url).then(done).catch(() => {
        const field = modal && modal.querySelector('[data-role="fav-share-url"]');
        if (field) { field.focus(); field.select(); }
        done();
      });
    } else {
      const field = modal && modal.querySelector('[data-role="fav-share-url"]');
      if (field) { field.focus(); field.select(); }
      done();
    }
  });
}

// Snapshot the CURRENT favorite ids into a server-side share record and open the
// share-options modal (Email / WhatsApp / Viber / Telegram / SMS / Copy) with the
// returned /favorite/{token}/ URL. Works for logged-in users (favSet already
// mirrors the account list) and guests (favSet = localStorage).
async function createFavoritesShare(btn) {
  // Reverse so the shared list is newest-first too, matching the favorites
  // page render order (favSet is oldest → newest insertion order).
  const ids = Array.from(favSet).map(Number).filter((n) => Number.isFinite(n) && n > 0).reverse();
  const label = btn.querySelector('[data-role="share-favorites-label"]');
  const defaultText = label ? label.textContent : '';
  const say = (msg) => { if (label) label.textContent = msg; };

  if (!ids.length) {
    say(btn.dataset.emptyText || t('fav_empty', 'Nu ai favorite'));
    setTimeout(() => say(defaultText), 2200);
    return;
  }

  const ajaxUrl = (AP_DATA && AP_DATA.ajaxUrl) || '/wp-admin/admin-ajax.php';
  // Immediate, synchronous loading feedback so the click feels responsive even
  // before the network round-trip resolves.
  btn.disabled = true;
  btn.classList.add('is-loading');
  say(btn.dataset.loadingText || t('fav_creating_link', 'Se creează linkul…'));

  const body = new URLSearchParams();
  body.set('action', 'atpl_create_fav_share');
  body.set('nonce', btn.dataset.nonce || '');
  body.set('lang', btn.dataset.lang || (AP_DATA && AP_DATA.lang) || 'ro');
  body.set('ids', JSON.stringify(ids));

  let data;
  try {
    const res = await fetch(ajaxUrl, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' },
      body: body.toString(),
    });
    data = await res.json();
  } catch {
    data = { success: false };
  }

  btn.disabled = false;
  btn.classList.remove('is-loading');
  say(defaultText);

  if (!data || !data.success || !data.data || !data.data.url) {
    const msg = (data && data.data && data.data.message) ? data.data.message : t('error_generic', 'A apărut o eroare');
    say(msg);
    setTimeout(() => say(defaultText), 2600);
    return;
  }

  const url = data.data.url;
  const shareTitle = data.data.title || document.title;

  // Show the nice share-options modal (Email / WhatsApp / Viber / Telegram / SMS
  // / Copy). When the modal isn't on the page for some reason, fall back to the
  // clipboard so the flow still works.
  const modal = openShareModal(url, shareTitle);
  if (!modal) {
    const confirmMsg = data.data.message || t('fav_link_copied', 'Link copiat!');
    const done = () => { say(confirmMsg); setTimeout(() => say(defaultText), 3000); };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(url).then(done).catch(done);
    } else {
      window.prompt('', url);
      done();
    }
  }
}

function initFavoritesShare() {
  delegate(document, 'click', '[data-action="share-favorites"]', (e, btn) => {
    e.preventDefault();
    createFavoritesShare(btn);
  });
  initShareModalOptions();
}

export function initFavorites() {
  reconcileFavs(); // merge/adopt account favorites before anything reads favSet
  syncAllVisible();
  populateAccountFavorites();
  initFavoritesShare();
  // Badge depends on favSet only (not on which cards are rendered), so we
  // sync it once on init and again from toggleFav. Keep it OUT of the
  // mutation-observer path.
  syncBadges();
  delegate(document, 'click', '[data-action="favorite"]', (e, el) => {
    // Stop propagation so the click doesn't bubble to a wrapping card
    // anchor (catalog cards have a __track-link <a> behind the slider).
    e.preventDefault();
    e.stopPropagation();
    const id = Number(el.dataset.id);
    if (!Number.isFinite(id)) return;
    const nowFav = toggleFav(id);
    pulse(el);
    if (nowFav) burstHearts(el);
  });

  // Re-sync when listing grids re-render
  const observer = new MutationObserver(() => syncAllVisible());
  observer.observe(document.body, { childList: true, subtree: true });
}
